import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Send, Cpu, Play, Rocket, CheckCircle2, AlertTriangle,
  Settings, X, Loader2, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OperatorChat = ({ blueprintId, blueprintVersion, onPatchApplied, onSimulate, onDeploy }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingPatch, setPendingPatch] = useState(null);
  const [patchSummary, setPatchSummary] = useState('');
  const [validationReport, setValidationReport] = useState(null);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addMessage = (role, content, meta = {}) => {
    setMessages(prev => [...prev, { id: Date.now(), role, content, ...meta }]);
  };

  const handleSend = async (mode = null) => {
    const text = input.trim();
    if (!text) return;

    const contextMode = mode || (text.toLowerCase().startsWith('explain') || text.endsWith('?') ? 'explain' : 'propose_patch');

    addMessage('user', text);
    setInput('');
    setLoading(true);
    setPendingPatch(null);

    try {
      const res = await axios.post(`${API}/operator-chat`, {
        blueprint_id: blueprintId,
        blueprint_version: blueprintVersion,
        message: text,
        context_mode: contextMode,
      });

      const data = res.data;

      if (data.open_integrations) {
        addMessage('assistant', data.assistant_message, { type: 'secret_warning' });
        setShowIntegrations(true);
      } else if (data.proposed_patch) {
        addMessage('assistant', data.assistant_message, { type: 'patch', summary: data.patch_summary });
        setPendingPatch(data.proposed_patch);
        setPatchSummary(data.patch_summary || '');
        setValidationReport(data.validation_report);
      } else {
        addMessage('assistant', data.assistant_message);
      }
    } catch (err) {
      addMessage('assistant', 'Failed to process request. Try again.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPatch = async () => {
    if (!pendingPatch) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/operator-chat/apply/${blueprintId}`, {
        blueprint_version: blueprintVersion,
        proposed_patch: pendingPatch,
      });
      addMessage('system', `Patch applied. New version: v${res.data.new_version}. ${res.data.change_summary}`);
      setPendingPatch(null);
      setPatchSummary('');
      onPatchApplied?.(res.data.new_version);
      toast.success('Patch applied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply patch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#080808] border-l border-white/5" data-testid="operator-chat">
      {/* Header */}
      <div className="p-3 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Operator Chat</span>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground">v{blueprintVersion}</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Cpu className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-30" />
            <p className="text-xs text-muted-foreground">Ask questions or request changes to your blueprint.</p>
            <div className="flex flex-wrap gap-1.5 justify-center mt-3">
              {['What does this do?', 'Add email notification', 'Change the output schema'].map(q => (
                <button key={q} onClick={() => { setInput(q); }} className="text-[10px] px-2 py-1 bg-white/5 border border-white/10 text-muted-foreground hover:text-white hover:border-white/20 transition-colors">{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-2.5 text-sm ${
              msg.role === 'user' ? 'bg-emerald-500/10 border border-emerald-500/20' :
              msg.role === 'system' ? 'bg-blue-500/10 border border-blue-500/20' :
              msg.type === 'error' ? 'bg-red-500/10 border border-red-500/20' :
              msg.type === 'secret_warning' ? 'bg-amber-500/10 border border-amber-500/20' :
              'bg-white/5 border border-white/10'
            }`}>
              {msg.type === 'secret_warning' && (
                <div className="flex items-center gap-1.5 mb-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-[10px] text-amber-400 font-mono uppercase">Security Warning</span>
                </div>
              )}
              <p className="text-xs leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              {msg.type === 'patch' && msg.summary && (
                <p className="text-[10px] text-emerald-400 mt-1.5 pt-1.5 border-t border-white/5 font-mono">{msg.summary}</p>
              )}
              {msg.type === 'secret_warning' && (
                <Button onClick={() => window.open('/crm-settings', '_blank')} variant="outline" size="sm" className="mt-2 border-amber-500/20 text-amber-400 text-[10px] h-6 rounded-sm">
                  <Settings className="w-3 h-3 mr-1" /> Open Integrations
                </Button>
              )}
            </div>
          </div>
        ))}
        <div ref={scrollRef} />
      </div>

      {/* Pending Patch Preview */}
      {pendingPatch && (
        <div className="border-t border-white/5 p-3 bg-emerald-500/5 space-y-2" data-testid="patch-preview">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-emerald-400 font-mono uppercase">Proposed Changes</span>
            <button onClick={() => { setPendingPatch(null); setPatchSummary(''); }} className="p-0.5 text-muted-foreground hover:text-white"><X className="w-3 h-3" /></button>
          </div>
          <p className="text-xs text-muted-foreground">{patchSummary}</p>
          <div className="text-[10px] font-mono text-muted-foreground bg-black/30 p-2 max-h-20 overflow-auto">
            {Object.entries(pendingPatch).filter(([_, v]) => v !== null).map(([k]) => (
              <div key={k} className="text-emerald-400/60">+ {k}</div>
            ))}
          </div>
          {validationReport && (
            <div className="flex items-center gap-1.5">
              {validationReport.valid ? (
                <><CheckCircle2 className="w-3 h-3 text-emerald-400" /><span className="text-[10px] text-emerald-400">Valid</span></>
              ) : (
                <><AlertTriangle className="w-3 h-3 text-amber-400" /><span className="text-[10px] text-amber-400">{validationReport.blockers?.length || 0} blockers, {validationReport.warnings?.length || 0} warnings</span></>
              )}
            </div>
          )}
          <div className="flex gap-1.5">
            <Button onClick={handleApplyPatch} disabled={loading || (validationReport && !validationReport.valid)} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-7 text-[10px] flex-1" data-testid="apply-patch-btn">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" /> Apply Patch</>}
            </Button>
            <Button onClick={() => onSimulate?.()} size="sm" variant="outline" className="border-white/10 rounded-sm h-7 text-[10px]" data-testid="sim-btn">
              <Play className="w-3 h-3 mr-1" /> Simulate
            </Button>
            <Button onClick={() => onDeploy?.()} size="sm" variant="outline" className="border-white/10 rounded-sm h-7 text-[10px]" data-testid="deploy-btn">
              <Rocket className="w-3 h-3 mr-1" /> Deploy
            </Button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-white/5 p-3">
        <div className="flex gap-1.5">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Ask or request changes..."
            className="flex-1 bg-black/30 border-white/10 rounded-sm h-8 text-xs"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button onClick={() => handleSend()} disabled={loading || !input.trim()} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-8 px-2.5" data-testid="chat-send">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <div className="flex gap-1.5 mt-1.5">
          <button onClick={() => handleSend('explain')} className="text-[9px] px-1.5 py-0.5 bg-white/5 text-muted-foreground hover:text-white transition-colors" disabled={loading || !input.trim()}>Explain</button>
          <button onClick={() => handleSend('propose_patch')} className="text-[9px] px-1.5 py-0.5 bg-white/5 text-muted-foreground hover:text-white transition-colors" disabled={loading || !input.trim()}>Propose Changes</button>
        </div>
      </div>
    </div>
  );
};

export default OperatorChat;
